import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import RichContent from '../components/RichContent';
import { useNavigate } from 'react-router-dom';
import { useAnimations } from '../context/AnimationContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  Plus,
  ArrowLeft,
  Clock,
  MapPin,
  User,
  Users,
  Trash2,
  Video,
  ExternalLink,
  History,
  PlayCircle,
  CalendarDays,
  CalendarPlus,
  Mail,
  CheckCircle,
  UserCheck,
  Loader2,
  XCircle,
  Play,
  Edit,
  MessageSquare,
  Send,
  Reply,
  CornerDownRight
} from 'lucide-react';
import FramedAvatar from '../components/FramedAvatar';
import DateTimePicker from '../components/DateTimePicker';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { format, isSameDay, startOfMonth, endOfMonth, parseISO, isAfter, isBefore, isWithinInterval } from 'date-fns';
import { fr } from 'date-fns/locale';

const CalendarPage = () => {
  const { user, api, canCreateEvents } = useAuth();
  const navigate = useNavigate();
  const { showXpGain, triggerNavigation } = useAnimations();
  
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [attendedEvents, setAttendedEvents] = useState({});
  const [attendingEventId, setAttendingEventId] = useState(null);
  
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    event_link: '',
    recurrence: '',
    recurrence_count: '',
    recurrence_end_date: ''
  });

  useEffect(() => {
    fetchEvents();
    fetchAllEvents();
    fetchMyAttendance();
  }, [selectedDate]);

  const fetchEvents = async () => {
    try {
      const start = startOfMonth(selectedDate).toISOString();
      const end = endOfMonth(selectedDate).toISOString();
      const response = await api.get(`/events?start_date=${start}&end_date=${end}`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllEvents = async () => {
    try {
      // Get a wider range for past/future events
      const pastStart = new Date();
      pastStart.setMonth(pastStart.getMonth() - 3);
      const futureEnd = new Date();
      futureEnd.setMonth(futureEnd.getMonth() + 6);
      const response = await api.get(`/events?start_date=${pastStart.toISOString()}&end_date=${futureEnd.toISOString()}`);
      setAllEvents(response.data);
    } catch (error) {
      console.error('Error fetching all events:', error);
    }
  };

  const fetchMyAttendance = async () => {
    try {
      const response = await api.get('/events/my-attendance');
      // Store full attendance data including status
      const attendanceMap = {};
      response.data.forEach(a => {
        attendanceMap[a.event_id] = {
          status: a.status || 'registered',
          xp_awarded: a.xp_awarded || false
        };
      });
      setAttendedEvents(attendanceMap);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const handleAttendEvent = async (eventId) => {
    setAttendingEventId(eventId);
    try {
      const response = await api.post(`/events/${eventId}/attend`);
      const status = response.data.status || 'registered';
      
      // Show appropriate message based on status
      if (status === 'checked_in') {
        toast.success(response.data.message || 'Présence confirmée ! +25 XP 🎉');
        showXpGain(25); // Animation XP
      } else if (status === 'registered') {
        toast.info(response.data.message || 'Inscription enregistrée !');
      } else if (status === 'too_early') {
        toast.info(response.data.message);
        return;
      } else if (status === 'too_late' || status === 'ended') {
        toast.warning(response.data.message);
        return;
      }
      
      setAttendedEvents(prev => ({
        ...prev,
        [eventId]: { status, xp_awarded: status === 'checked_in' }
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setAttendingEventId(null);
    }
  };

  const handleCancelAttendance = async (eventId) => {
    setAttendingEventId(eventId);
    try {
      const response = await api.delete(`/events/${eventId}/attend`);
      toast.success(response.data.message || 'Participation annulée');
      
      setAttendedEvents(prev => {
        const newState = { ...prev };
        delete newState[eventId];
        return newState;
      });
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    } finally {
      setAttendingEventId(null);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    if (!newEvent.title || !newEvent.start_time || !newEvent.end_time) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        start_time: new Date(newEvent.start_time).toISOString(),
        end_time: new Date(newEvent.end_time).toISOString(),
        location: newEvent.location,
        event_link: newEvent.event_link || null,
        recurrence: newEvent.recurrence || null,
        recurrence_count: newEvent.recurrence_count ? parseInt(newEvent.recurrence_count) : null,
        recurrence_end_date: newEvent.recurrence_end_date ? new Date(newEvent.recurrence_end_date).toISOString() : null,
      };

      await api.post('/events', eventData);
      const suffix = newEvent.recurrence ? ' (série récurrente créée)' : '';
      toast.success('Événement créé !' + suffix);
      setCreateDialogOpen(false);
      setNewEvent({ title: '', description: '', start_time: '', end_time: '', location: '', event_link: '', recurrence: '', recurrence_count: '', recurrence_end_date: '' });
      fetchEvents();
      fetchAllEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleAddToCalendar = async (eventId, calendarType) => {
    try {
      const response = await api.get(`/events/${eventId}/calendar-links`);
      const links = response.data;
      
      let url;
      switch (calendarType) {
        case 'google':
          url = links.google_calendar;
          break;
        case 'outlook':
          url = links.outlook_hotmail;
          break;
        case 'office365':
          url = links.outlook_office365;
          break;
        default:
          return;
      }
      
      window.open(url, '_blank');
      toast.success(`Ouverture de ${calendarType === 'google' ? 'Google Calendar' : 'Outlook'}...`);
    } catch (error) {
      toast.error('Erreur lors de la génération du lien');
    }
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = parseISO(event.start_time);
      return isSameDay(eventDate, date);
    });
  };

  const now = new Date();

  // Categorize events
  const upcomingEvents = allEvents.filter(e => isAfter(parseISO(e.start_time), now));
  const pastEvents = allEvents.filter(e => isBefore(parseISO(e.end_time), now));
  const liveEvents = allEvents.filter(e => {
    const start = parseISO(e.start_time);
    const end = parseISO(e.end_time);
    return isWithinInterval(now, { start, end });
  });

  const selectedDateEvents = getEventsForDate(selectedDate);

  const modifiers = {
    hasEvent: (date) => getEventsForDate(date).length > 0
  };

  const modifiersStyles = {
    hasEvent: {
      backgroundColor: 'hsl(var(--primary) / 0.2)',
      borderRadius: '50%'
    }
  };

  const EventCard = ({ event, showDate = false, isLive = false, isPast = false }) => {
    const attendance = attendedEvents[event.id];
    const hasRegistered = !!attendance;
    const hasCheckedIn = attendance?.status === 'checked_in' || attendance?.status === 'validated';
    const isAttending = attendingEventId === event.id;
    
    // Determine button state
    const getButtonConfig = () => {
      if (isPast) return null;
      
      if (hasCheckedIn) {
        // Already checked in - show cancel button
        return {
          type: 'cancel',
          label: 'Annuler',
          className: 'border-red-500/50 text-red-500 hover:bg-red-500/10',
          icon: XCircle
        };
      }
      
      if (hasRegistered && !isLive) {
        // Registered but event not live yet - show waiting state + cancel
        return {
          type: 'waiting',
          label: 'Inscrit',
          className: 'border-yellow-500/50 text-yellow-500 bg-yellow-500/10',
          icon: Clock,
          showCancel: true
        };
      }
      
      if (hasRegistered && isLive) {
        // Registered and event is live - show check-in button
        return {
          type: 'checkin',
          label: 'Je suis là !',
          className: 'bg-green-500 hover:bg-green-600 text-white animate-pulse',
          icon: UserCheck
        };
      }
      
      if (isLive) {
        // Event is live, not registered - show check-in button
        return {
          type: 'checkin',
          label: 'Je suis là !',
          className: 'bg-green-500 hover:bg-green-600 text-white animate-pulse',
          icon: UserCheck
        };
      }
      
      // Event not started - show RSVP button
      return {
        type: 'rsvp',
        label: 'Je participe',
        className: 'bg-primary hover:bg-primary/90 text-primary-foreground font-medium',
        icon: UserCheck
      };
    };
    
    const buttonConfig = getButtonConfig();
    
    return (
    <div
      className={`p-4 rounded-lg border transition-all cursor-pointer animate-fade-in ${
        isLive 
          ? 'bg-red-500/10 border-red-500/30 hover:border-red-500/50' 
          : isPast 
            ? 'bg-secondary/20 border-border/30 opacity-70 hover:opacity-100' 
            : 'bg-secondary/30 border-border/50 hover:border-primary/30'
      }`}
      onClick={() => navigate(`/calendar/event/${event.id}`)}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          {isLive && (
            <Badge className="bg-red-500 text-white mb-2 animate-pulse">
              <PlayCircle className="w-3 h-3 mr-1" />
              EN DIRECT
            </Badge>
          )}
          {hasCheckedIn && (
            <Badge className="bg-green-500/20 text-green-500 border-green-500/30 mb-2">
              <CheckCircle className="w-3 h-3 mr-1" />
              Présence confirmée (+25 XP)
            </Badge>
          )}
          {hasRegistered && !hasCheckedIn && !isPast && (
            <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30 mb-2">
              <Clock className="w-3 h-3 mr-1" />
              {isLive ? 'Confirmez votre présence !' : 'Inscrit - En attente'}
            </Badge>
          )}
          <h4 className="font-semibold mb-1">{event.title}</h4>
          <div className="space-y-1 text-sm text-muted-foreground">
            {showDate && (
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                <span>{format(parseISO(event.start_time), 'EEEE d MMMM yyyy', { locale: fr })}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>
                {format(parseISO(event.start_time), 'HH:mm')} - {format(parseISO(event.end_time), 'HH:mm')}
              </span>
            </div>
            {event.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{event.location}</span>
              </div>
            )}
          </div>
        </div>
        {/* Action buttons - aligned properly */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border/30">
          {/* Action buttons based on state */}
          {buttonConfig && buttonConfig.type !== 'cancel' && (
            <Button 
              size="sm" 
              variant={buttonConfig.type === 'checkin' || buttonConfig.type === 'rsvp' ? "default" : "outline"}
              className={buttonConfig.className}
              onClick={(e) => { e.stopPropagation(); handleAttendEvent(event.id); }}
              disabled={isAttending || buttonConfig.type === 'waiting'}
              data-testid={`attend-event-${event.id}`}
            >
              {isAttending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <buttonConfig.icon className="w-4 h-4 mr-1" />
              )}
              {buttonConfig.label}
            </Button>
          )}
          {/* Cancel button */}
          {buttonConfig && (buttonConfig.type === 'cancel' || buttonConfig.showCancel) && (
            <Button 
              size="sm" 
              variant="outline"
              className="border-red-500/50 text-red-500 hover:bg-red-500/10"
              onClick={(e) => { e.stopPropagation(); handleCancelAttendance(event.id); }}
              disabled={isAttending}
              data-testid={`cancel-attend-${event.id}`}
            >
              {isAttending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-1" />
              )}
              Annuler
            </Button>
          )}
          {!isPast && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" className="text-xs h-8">
                  <CalendarPlus className="w-4 h-4 mr-1" />
                  Agenda
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Ajouter à mon calendrier</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAddToCalendar(event.id, 'google'); }}>
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="currentColor" d="M19.5 4h-3V2.5a.5.5 0 0 0-1 0V4h-7V2.5a.5.5 0 0 0-1 0V4h-3A2.5 2.5 0 0 0 2 6.5v13A2.5 2.5 0 0 0 4.5 22h15a2.5 2.5 0 0 0 2.5-2.5v-13A2.5 2.5 0 0 0 19.5 4zM4.5 5h15a1.5 1.5 0 0 1 1.5 1.5V8H3V6.5A1.5 1.5 0 0 1 4.5 5zM19.5 21h-15A1.5 1.5 0 0 1 3 19.5V9h18v10.5a1.5 1.5 0 0 1-1.5 1.5z"/></svg>
                  Google Calendar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAddToCalendar(event.id, 'outlook'); }}>
                  <Mail className="w-4 h-4 mr-2" />
                  Outlook / Hotmail
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAddToCalendar(event.id, 'office365'); }}>
                  <Mail className="w-4 h-4 mr-2" />
                  Office 365
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {event.event_link && !isPast && (
            <Button 
              size="sm" 
              variant={isLive ? "default" : "outline"}
              className={`h-8 ${isLive ? "bg-red-500 hover:bg-red-600 text-white" : ""}`}
              onClick={(e) => { e.stopPropagation(); window.open(event.event_link, '_blank'); }}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              {isLive ? 'Rejoindre' : 'Lien'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="calendar-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center gold-glow">
                <CalendarIcon className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading font-bold text-base sm:text-xl">Calendrier</h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Événements de la communauté</p>
              </div>
            </div>
          </div>

          {canCreateEvents && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 gold-glow shrink-0" data-testid="create-event-button">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Créer un événement</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="font-heading">Nouvel événement</DialogTitle>
                  <DialogDescription>Créez un événement pour la communauté</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateEvent} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titre *</Label>
                    <Input
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                      placeholder="Nom de l'événement"
                      className="bg-secondary/50"
                      data-testid="event-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <RichTextEditor
                      value={newEvent.description}
                      onChange={(description) => setNewEvent({ ...newEvent, description })}
                      rows={6}
                      placeholder="Programme, intervenants, lien de connexion…"
                      testId="event-description-editor"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Début *</Label>
                      <DateTimePicker
                        value={newEvent.start_time}
                        onChange={(start_time) => setNewEvent({ ...newEvent, start_time })}
                        testId="event-start"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fin *</Label>
                      <DateTimePicker
                        value={newEvent.end_time}
                        onChange={(end_time) => setNewEvent({ ...newEvent, end_time })}
                        testId="event-end"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Lieu</Label>
                    <Input
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                      placeholder="En ligne / Adresse"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lien de l'événement (Zoom, Meet...)</Label>
                    <Input
                      value={newEvent.event_link}
                      onChange={(e) => setNewEvent({ ...newEvent, event_link: e.target.value })}
                      placeholder="https://..."
                      className="bg-secondary/50"
                    />
                  </div>
                  
                  {/* Recurrence options - admin/major only */}
                  {(user.role === 'admin' || user.role === 'major') && (
                    <div className="space-y-3 border rounded-lg p-3 bg-secondary/20">
                      <Label className="text-sm font-medium">Recurrence</Label>
                      <Select value={newEvent.recurrence || 'none'} onValueChange={(v) => setNewEvent({ ...newEvent, recurrence: v === 'none' ? '' : v })}>
                        <SelectTrigger className="bg-secondary/50" data-testid="recurrence-select">
                          <SelectValue placeholder="Pas de recurrence" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Pas de recurrence</SelectItem>
                          <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          <SelectItem value="biweekly">Bimensuel (tous les 15 jours)</SelectItem>
                          <SelectItem value="monthly">Mensuel</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {newEvent.recurrence && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Nombre d'occurrences</Label>
                            <Input
                              type="number"
                              min="2"
                              max="52"
                              value={newEvent.recurrence_count}
                              onChange={(e) => setNewEvent({ ...newEvent, recurrence_count: e.target.value, recurrence_end_date: '' })}
                              placeholder="Ex: 10"
                              className="bg-secondary/50"
                              data-testid="recurrence-count-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Ou date de fin</Label>
                            <Input
                              type="date"
                              value={newEvent.recurrence_end_date}
                              onChange={(e) => setNewEvent({ ...newEvent, recurrence_end_date: e.target.value, recurrence_count: '' })}
                              className="bg-secondary/50"
                              data-testid="recurrence-end-date-input"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Annuler</Button>
                    <Button type="submit" className="bg-primary text-primary-foreground" data-testid="submit-event-button">Créer</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar Widget - Improved Design */}
          <Card className="lg:col-span-2 overflow-hidden border-0 shadow-xl">
            <div className="bg-gradient-to-br from-primary/10 via-background to-secondary/20 p-1">
              <CardContent className="p-4 sm:p-6 bg-card/80 backdrop-blur-sm rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-primary" />
                    Calendrier
                  </h2>
                  <Badge variant="outline" className="bg-primary/10 border-primary/30">
                    {format(selectedDate, 'MMMM yyyy', { locale: fr })}
                  </Badge>
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  locale={fr}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  className="w-full rounded-xl [&_.rdp-months]:justify-center [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_.rdp-head_cell]:text-primary/60 [&_.rdp-head_cell]:font-medium [&_.rdp-cell]:p-1 [&_.rdp-day]:w-full [&_.rdp-day]:h-10 [&_.rdp-day]:rounded-lg [&_.rdp-day:hover]:bg-primary/20 [&_.rdp-day_selected]:bg-primary [&_.rdp-day_selected]:text-primary-foreground [&_.rdp-day_today]:border-2 [&_.rdp-day_today]:border-primary/50 [&_.rdp-nav]:gap-1 [&_.rdp-nav_button]:w-8 [&_.rdp-nav_button]:h-8 [&_.rdp-nav_button]:rounded-lg [&_.rdp-nav_button:hover]:bg-primary/20 [&_.rdp-caption]:py-2"
                  data-testid="calendar-widget"
                />
              </CardContent>
            </div>
          </Card>

          {/* Events for Selected Date */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-br from-secondary/30 via-background to-primary/5 p-1">
              <div className="bg-card/80 backdrop-blur-sm rounded-lg h-full">
                <CardHeader className="pb-2">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-primary" />
                    {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[350px] pr-4">
                    {selectedDateEvents.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucun événement ce jour</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedDateEvents.map((event) => {
                          const isLive = isWithinInterval(now, { start: parseISO(event.start_time), end: parseISO(event.end_time) });
                          const isPast = isBefore(parseISO(event.end_time), now);
                          return <EventCard key={event.id} event={event} isLive={isLive} isPast={isPast} />;
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabbed Events List */}
        <Card className="mt-8 border-0 shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-primary/5 via-background to-secondary/10 p-1">
            <div className="bg-card/80 backdrop-blur-sm rounded-lg">
              <CardHeader className="pb-0">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="bg-secondary/50 w-full justify-start overflow-x-auto" data-testid="calendar-tabs">
                <TabsTrigger value="upcoming" className="flex items-center gap-1.5 shrink-0 text-xs sm:text-sm" data-testid="tab-upcoming">
                  <CalendarDays className="w-4 h-4" />
                  À venir ({upcomingEvents.length})
                </TabsTrigger>
                <TabsTrigger value="live" className="flex items-center gap-1.5 shrink-0 text-xs sm:text-sm" data-testid="tab-live">
                  <PlayCircle className="w-4 h-4" />
                  En cours ({liveEvents.length})
                  {liveEvents.length > 0 && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
                </TabsTrigger>
                <TabsTrigger value="past" className="flex items-center gap-1.5 shrink-0 text-xs sm:text-sm" data-testid="tab-past">
                  <History className="w-4 h-4" />
                  Passés ({pastEvents.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Upcoming Events */}
            {activeTab === 'upcoming' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingEvents.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun événement à venir</p>
                  </div>
                ) : (
                  upcomingEvents.slice(0, 9).map((event, index) => (
                    <div key={event.id} style={{ animationDelay: `${index * 0.05}s` }}>
                      <EventCard event={event} showDate />
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Live Events */}
            {activeTab === 'live' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {liveEvents.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    <PlayCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun événement en cours</p>
                    <p className="text-sm mt-2">Les événements en direct apparaîtront ici</p>
                  </div>
                ) : (
                  liveEvents.map((event, index) => (
                    <div key={event.id} style={{ animationDelay: `${index * 0.05}s` }}>
                      <EventCard event={event} showDate isLive />
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Past Events */}
            {activeTab === 'past' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pastEvents.length === 0 ? (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun événement passé</p>
                  </div>
                ) : (
                  pastEvents.slice(0, 12).map((event, index) => (
                    <div key={event.id} style={{ animationDelay: `${index * 0.05}s` }}>
                      <EventCard event={event} showDate isPast />
                    </div>
                  ))
                )}
              </div>
            )}
          </CardContent>
            </div>
          </div>
        </Card>
      </main>

    </div>
  );
};

export default CalendarPage;
